<template>
    <div>
        <test-template :testName="testName" :testId="testId" :test="test" :opposedAnswers="opposedAnswers" multidimensional>
            <template #questions>
                <question-card :question="cQ" v-model="questionResult" :progress="$store.state.tests.currentSlide" :questionnaireLength="$store.state.tests.test.length"  @answerQuestion="() => { $store.commit('tests/incrementSlide') }" @submitQuestionnaire="saveTestResults"></question-card>
            </template>
            <template #results>
                <div key="topGraph" class="first">
                    <h3 class="subtitle-card text-center">{{ $t('tests.testResults') }} - {{ testName.toUpperCase() }}</h3>
                    <column-chart :lastYearResults="$store.state.tests.previousTestResults.slice(-12)" :testName="testName"></column-chart>
                </div>
                <div key="recapCard" class="second">
                    <h4>{{ $t('tests.results') }} {{ month }}</h4>
                    <b-card class="neu-card mean-result col-lg-7">
                        <h3 class="subtitle-card"> {{ $t('tests.well-being') }}</h3>
                        <b-row class="h-100">
                            <b-col xl="6" class="my-auto">
                                <gauge-chart :labels="[$t('tests.well-being')]" :result="DBResults.generalResult" :customColour="resultInfo.color"></gauge-chart>
                            </b-col>
                            <b-col><p class="p-white" v-html="resultInfo.resultAnalysis"></p></b-col>
                        </b-row>
                    </b-card>
                </div>
                <div key="dimensions" class="third">
                    <h4>{{ $t('tests.dimensions') }}</h4>
                    <div class="col-lg-12 results-container">
                        <div v-for="d in DBResults.dimensions" :key="d.name" class="col-lg-8 results-card">
                            <b-col>
                                <b-card class="neu-card" v-b-toggle="d.id" :title="$t(d.name)">
                                    <div>
                                        <b-progress style="height: 0.5rem">
                                            <b-progress-bar :value="d.result" max=100 :style="`background-color: ${barColor(d.result)}`">
                                            </b-progress-bar>
                                        </b-progress><span> {{ d.result }}%</span>
                                        <div style="margin-top:1%;margin-bottom:-2%">
                                            <span class="text-center accordion-arrow-closed">
                                                <b-icon-chevron-down></b-icon-chevron-down>
                                            </span>
                                            <span class="text-center accordion-arrow-open">
                                                <b-icon-chevron-up></b-icon-chevron-up>
                                            </span>
                                            <b-collapse :id="d.id" class="mt-2 mb-3 text-left" accordion="accordion">
                                                <p class="card-text text-justify" v-html="$t(d.message)"></p>
                                            </b-collapse>
                                        </div>
                                    </div>
                                </b-card>
                            </b-col>
                        </div>
                    </div>
                </div>
            </template>
        </test-template>
    </div>
</template>

<script>
import TestTemplate from './TestTemplate.vue'
import { BCollapse, BProgress, BProgressBar } from 'bootstrap-vue'
import selfEvaluationTestMixin from '@/mixins/selfEvaluationTest.mixin'
import { TEST_RYFF_SCALE } from '@/constants/patientServices'
import { GREEN_COLOUR, RED_COLOUR, YELLOW_COLOUR } from '@/constants/colours'

export default {
    mixins: [selfEvaluationTestMixin],
    components: {
        'test-template': TestTemplate,
        BCollapse,
        BProgress,
        BProgressBar,
        'gauge-chart': () => import('@/components/charts/GaugeChart.vue'),
        'column-chart': () => import('@/components/charts/ColumnChart.vue')
    },
    data () {
        const values = [
                {value: 7, text: this.$t('tests.answers.total-disagreement')},
                {value: 6, text: this.$t('tests.answers.some-disagreement')},
                {value: 5, text: this.$t('tests.answers.bit-disagreement')},
                {value: 4, text: this.$t('tests.answers.agreement-disagreement')},
                {value: 3, text: this.$t('tests.answers.bit-agreement')},
                {value: 2, text: this.$t('tests.answers.some-agreement')},
                {value: 1, text: this.$t('tests.answers.total-agreement')}
        ]

        const test = [
            {
                dimension: 'tests.ryff-scale.dimensions.self-acceptance',
                highScore: 'tests.ryff-scale.scores.selfAcceptanceHigh',
                lowScore: 'tests.ryff-scale.scores.selfAcceptanceLow',
                qs: ['q1', 'q10', 'q13', 'q21', 'q24', 'q35', 'q41']
            },
            {
                dimension: 'tests.ryff-scale.dimensions.environmental-control',
                highScore: 'tests.ryff-scale.scores.environmentalControlHigh',
                lowScore: 'tests.ryff-scale.scores.environmentalControlLow',
                qs: ['q3', 'q12', 'q15', 'q23', 'q26', 'q36', 'q42']
            },
            {
                dimension: 'tests.ryff-scale.dimensions.personal-growth',
                highScore: 'tests.ryff-scale.scores.personalGrowthHigh',
                lowScore: 'tests.ryff-scale.scores.personalGrowthLow',
                qs: ['q2', 'q5', 'q14', 'q17', 'q25', 'q28', 'q37']
            },
            {
                dimension: 'tests.ryff-scale.dimensions.positive-relationships',
                highScore: 'tests.ryff-scale.scores.positiveRelationshipsHigh',
                lowScore: 'tests.ryff-scale.scores.positiveRelationshipsLow',
                qs: ['q4', 'q7', 'q16', 'q18', 'q27', 'q30', 'q38']
            },
            {
                dimension: 'tests.ryff-scale.dimensions.life-purposes',
                highScore: 'tests.ryff-scale.scores.lifePurposesHigh',
                lowScore: 'tests.ryff-scale.scores.lifePurposesLow',
                qs: ['q6', 'q9', 'q20', 'q29', 'q32', 'q33', 'q39']
            },
            {
                dimension: 'tests.ryff-scale.dimensions.autonomy',
                highScore: 'tests.ryff-scale.scores.autonomyHigh',
                lowScore: 'tests.ryff-scale.scores.autonomyLow',
                qs: ['q8', 'q11', 'q19', 'q22', 'q31', 'q34', 'q40']
            }
        ]

        const opposedAnswers = ['q1', 'q2', 'q3', 'q4', 'q6', 'q7', 'q11', 'q13', 'q17', 'q20', 'q21', 'q22', 'q23', 'q27', 'q29', 'q31', 'q35', 'q36', 'q37', 'q38', 'q40']
        return {
            test: test,
            values: values,
            testId: TEST_RYFF_SCALE,
            opposedAnswers: opposedAnswers,
            testName: this.$t('tests.ryff-scale.title'),
            multidimensional: true
        }
    },
    created () {
        this.createQuestions()
    },
    methods: {
        createQuestions () {
            var i = 1
            this.test.map(dimension => {
                dimension.id = `apartado-${i}`
                var questions = []
                dimension.qs.map(x => questions.push({
                    id: x,
                    question: this.$t(`tests.ryff-scale.questions.${x}`),
                    answer: this.values,
                    result: null
                }))
                i = i + 1
                this.$set(dimension, 'questions', questions)
            })
        },
        saveTestResults () {
            var dimensions = []
            for (let dimension of this.test) {
                var dimensionValues = this.calculateMultidimensionalDimPoints(dimension)
                var result = Math.round(dimensionValues.sum * 100 / dimensionValues.total)
                var messageKey = result < 60 ? dimension.lowScore : dimension.highScore
                dimensions.push({'result': result, 'id': dimension.id, 'name': dimension.dimension, 'message': messageKey})
            }
            this.save(dimensions, Math.round(dimensions.map(x => x.result).reduce((x, y) => x + y, 0) / 6))
        },
        barColor (result) {
            return result < 50 ? RED_COLOUR : result < 70 ? YELLOW_COLOUR : GREEN_COLOUR
        }
    }
}
</script>


// WEBPACK FOOTER //
// src/components/views/selfEvaluationTests/RyffScaleTest.vue